// The leads directory's filters <-> its URL query string.
//
// Every filter on the directory (pages/CompanyDirectory.jsx) is mirrored into
// the address bar, so Back from a lead lands on the same filtered list instead
// of the unfiltered directory, and a filtered list can be bookmarked or pasted.
// Only filters that differ from the default are written, so the plain
// directory keeps its plain URL.
//
//   q=brick                      search box
//   state=IL&state=IN            states (the geo filter in state mode). Also
//                                the deep link the Market Map and the import
//                                wizard already use: ?state=OK
//   city=IL|Chicago              cities (geo filter in city mode), cityKey form
//   size= volume= website= enriched=   the band chips, repeated per value
//   flag=certified|active-projects|volume-disclosed   the quality flags
//   score=40                     minimum intent score
//   contact=any                  "Has contact" switched OFF (on is the default)
//   trade= source= outreach=     the facet chips, repeated per value
//
// Lists are repeated keys rather than comma-joined, because city keys and
// trades can themselves contain commas. ?region=<slug> (global search's deep
// link) is not a filter; the page turns it into state= once rows arrive.
// Nothing here touches React.

export const EMPTY_DIRECTORY_FILTERS = Object.freeze({
  geo: { mode: 'state', states: [], cities: [] },
  query: '',
  bands: { size: [], volume: [], website: [], enriched: [] },
  flags: { certified: false, activeProjects: false, volumeDisclosed: false },
  minScore: 0,
  reachableOnly: true,
  trades: [],
  sources: [],
  outreach: [],
})

const BAND_KEYS = ['size', 'volume', 'website', 'enriched']
const FLAG_PARAMS = { certified: 'certified', activeProjects: 'active-projects', volumeDisclosed: 'volume-disclosed' }
const FLAG_BY_PARAM = Object.fromEntries(Object.entries(FLAG_PARAMS).map(([k, v]) => [v, k]))
const LIST_PARAMS = { trade: 'trades', source: 'sources', outreach: 'outreach' }

// Every key this module owns. Anything else in the URL is left alone.
export const DIRECTORY_FILTER_PARAMS = new Set([
  'q', 'state', 'city', ...BAND_KEYS, 'flag', 'score', 'contact', ...Object.keys(LIST_PARAMS),
])

function uniqueValues(params, key, transform = (v) => v) {
  const out = []
  for (const raw of params.getAll(key)) {
    const v = transform(String(raw || '').trim())
    if (v && !out.includes(v)) out.push(v)
  }
  return out
}

/** URLSearchParams (or a query string) -> the directory's full filter state. */
export function filtersFromParams(input) {
  const params = input instanceof URLSearchParams ? input : new URLSearchParams(input || '')
  const states = uniqueValues(params, 'state', (v) => v.toUpperCase())
  const cities = uniqueValues(params, 'city')
  const flags = { ...EMPTY_DIRECTORY_FILTERS.flags }
  for (const p of uniqueValues(params, 'flag')) if (FLAG_BY_PARAM[p]) flags[FLAG_BY_PARAM[p]] = true
  const score = Number(params.get('score'))
  const out = {
    // Cities win when both are present: city mode is the narrower cut.
    geo: cities.length ? { mode: 'city', states: [], cities } : { mode: 'state', states, cities: [] },
    query: String(params.get('q') || ''),
    bands: Object.fromEntries(BAND_KEYS.map((k) => [k, uniqueValues(params, k)])),
    flags,
    // The score slider runs 0-90 in steps of 5.
    minScore: Number.isFinite(score) && score > 0 ? Math.min(90, Math.round(score)) : 0,
    reachableOnly: params.get('contact') !== 'any',
  }
  for (const [param, field] of Object.entries(LIST_PARAMS)) out[field] = uniqueValues(params, param)
  return out
}

/**
 * The directory's filter state -> a query string. Starts from `keep` (the
 * current URL) so parameters this module does not own survive; every owned
 * key is rewritten from the state, and `drop` names keys to remove outright.
 */
export function filtersToParams(filters, { keep = '', drop = [] } = {}) {
  const f = { ...EMPTY_DIRECTORY_FILTERS, ...(filters || {}) }
  const params = new URLSearchParams(keep instanceof URLSearchParams ? keep.toString() : keep || '')
  for (const key of [...DIRECTORY_FILTER_PARAMS, ...drop]) params.delete(key)

  const query = String(f.query || '').trim()
  if (query) params.set('q', query)
  const geo = f.geo || EMPTY_DIRECTORY_FILTERS.geo
  if (geo.mode === 'city') for (const c of geo.cities || []) params.append('city', c)
  else for (const s of geo.states || []) params.append('state', s)
  for (const k of BAND_KEYS) for (const v of f.bands?.[k] || []) params.append(k, v)
  for (const [key, param] of Object.entries(FLAG_PARAMS)) if (f.flags?.[key]) params.append('flag', param)
  if (f.minScore > 0) params.set('score', String(f.minScore))
  if (f.reachableOnly === false) params.set('contact', 'any')
  for (const [param, field] of Object.entries(LIST_PARAMS)) for (const v of f[field] || []) params.append(param, v)
  return params.toString()
}
